import clsx from 'clsx'
import React from 'react'

import { SystemToast } from '@/components-latest/ui/Toaster/SystemToast/SystemToast'
import {
  TIME_BEFORE_UNMOUNT,
  TOAST_LIFETIME
} from '@/components-latest/ui/Toaster/Toaster.constants'
import { HeightT } from '@/components-latest/ui/Toaster/Toaster.types'
import { INotification } from '@/ducks/notifications/slices/notification.slice'
import { useIsDocumentHidden } from '@/hooks/useDocumentIsHidden/useDocumentIsHidden'

import Styles from '../Toaster.module.scss'

interface ISoloToast {
  interacting: boolean
  toast: INotification
  setHeights: React.Dispatch<React.SetStateAction<HeightT[]>>
  heights: HeightT[]
  index: number
  toasts: INotification[]
  onToastRemove: (toast: INotification) => void
  gap: number
  pileOffset: number
  expanded: boolean
}

const SoloToast: React.FC<ISoloToast> = props => {
  const {
    interacting,
    toast,
    setHeights,
    heights,
    index,
    toasts,
    onToastRemove,
    gap,
    pileOffset = 0,
    expanded
  } = props
  const [mounted, setMounted] = React.useState(false)
  const [removed, setRemoved] = React.useState(false)
  const [offsetBeforeRemove, setOffsetBeforeRemove] = React.useState(0)
  const [initialHeight, setInitialHeight] = React.useState(0)
  const toastRef = React.useRef<HTMLLIElement>(null)
  const dismissible = toast.dismissible !== false

  const heightIndex = React.useMemo(
    () => heights.findIndex(height => height.toastId === toast.id) || 0,
    [heights, toast.id]
  )
  const closeTimerStartTimeRef = React.useRef(0)
  const offset = React.useRef(0)
  const lastCloseTimerStartTimeRef = React.useRef(0)
  const toastsHeightBefore = React.useMemo(() => {
    return heights.reduce((prev, curr, reducerIndex) => {
      // Calculate offset up until current  toast
      if (reducerIndex >= heightIndex) {
        return prev
      }

      return prev + curr.height
    }, 0)
  }, [heights, heightIndex])
  const isDocumentHidden = useIsDocumentHidden()

  offset.current = React.useMemo(
    () => heightIndex * gap + toastsHeightBefore + pileOffset + gap,
    [gap, heightIndex, pileOffset, toastsHeightBefore]
  )

  React.useEffect(() => {
    setMounted(true)
  }, [])

  React.useLayoutEffect(() => {
    if (!mounted) return

    const toastNode = toastRef.current
    if (toastNode) {
      const originalHeight = toastNode.style.height
      toastNode.style.height = 'auto'
      const newHeight = toastNode.getBoundingClientRect().height
      toastNode.style.height = originalHeight

      setInitialHeight(newHeight)

      setHeights(heights => {
        const alreadyExists = heights.find(
          height => height.toastId === toast.id
        )
        if (!alreadyExists) {
          return [{ toastId: toast.id, height: newHeight }, ...heights]
        } else {
          return heights.map(height =>
            height.toastId === toast.id
              ? { ...height, height: newHeight }
              : height
          )
        }
      })
    }
  }, [mounted, setHeights, toast.id])

  const deleteToast = React.useCallback(() => {
    setRemoved(true)
    setOffsetBeforeRemove(offset.current)
    setHeights(h => h.filter(height => height.toastId !== toast.id))

    setTimeout(() => {
      onToastRemove(toast)
    }, TIME_BEFORE_UNMOUNT)
  }, [toast, onToastRemove, setHeights, offset])

  React.useEffect(() => {
    let timeoutId: NodeJS.Timeout
    let remainingTime = toast.duration ?? TOAST_LIFETIME

    // Pause the timer on each hover
    const pauseTimer = () => {
      if (lastCloseTimerStartTimeRef.current < closeTimerStartTimeRef.current) {
        // Get the elapsed time since the timer started
        const elapsedTime =
          new Date().getTime() - closeTimerStartTimeRef.current

        remainingTime = remainingTime - elapsedTime
      }

      lastCloseTimerStartTimeRef.current = new Date().getTime()
    }

    const startTimer = () => {
      if (remainingTime === Infinity) return

      closeTimerStartTimeRef.current = new Date().getTime()

      timeoutId = setTimeout(() => {
        deleteToast()
      }, remainingTime)
    }

    if (interacting || isDocumentHidden) {
      pauseTimer()
    } else {
      startTimer()
    }

    return () => clearTimeout(timeoutId)
  }, [deleteToast, interacting, isDocumentHidden])

  React.useEffect(() => {
    const toastNode = toastRef.current

    if (toastNode) {
      const height = toastNode.getBoundingClientRect().height

      setInitialHeight(height)
      setHeights(h => [{ toastId: toast.id, height }, ...h])

      return () =>
        setHeights(h => h.filter(height => height.toastId !== toast.id))
    }
  }, [setHeights, toast.id])

  if (toast.type !== 'system') return null

  return (
    <li
      data-testid="solo-toast"
      aria-live="polite"
      aria-atomic="true"
      role="status"
      tabIndex={0}
      ref={toastRef}
      className={clsx(Styles.soloToast)}
      data-mounted={mounted}
      data-removed={removed}
      data-index={index}
      data-dismissible={dismissible}
      data-expanded={expanded}
      onClick={deleteToast}
      style={
        {
          '--index': index,
          '--toasts-before': index,
          '--z-index': toasts.length - index,
          '--offset': `${removed ? offsetBeforeRemove : offset.current}px`,
          '--initial-height': `${initialHeight}px`
        } as React.CSSProperties
      }>
      <SystemToast {...toast} onClose={deleteToast} />
    </li>
  )
}

export { SoloToast }
